/**
 * palette.ts — THE central colour table (BL-52, the "Farb-Pflegetabelle").
 *
 * Single source of truth for every GAMEPLAY-ENTITY colour: the tower elements,
 * the enemy threat family, the player firewall, the synergies, and the two
 * avatars. The foundational mainframe / UI / danger anchors live in
 * core/constants.ts (`COLORS`) and are re-exported here so the whole palette
 * reads as one table.
 *
 * ┌─ EDIT GAMEPLAY COLOURS HERE — nothing else hardcodes one ─────────────────┐
 * │ • towers.json / enemies.json / synergies.json / firewalls.json carry NO   │
 * │   colour: DataLoader injects them from this table (keyed by element /     │
 * │   enemy-id / synergy-id).                                                 │
 * │ • designTokens' ELEMENT_COLORS / ELEMENT_RAMP derive from TOWER_PALETTE.  │
 * │ • Enemy.ts (shift tints) + StealthRenderer.ts read from here.             │
 * │ • OperatorMascot.ts reads AVATAR_PALETTE (frame status + figure badge).   │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Direction (North Star §4/§5, owner-signed — harmony preview =
 * docs/palette_harmony.html is the sign-off gate):
 *  • Towers = a COOL, varied, "edel" family (sat-discipline, muted bodies /
 *    bright cores) — mutually identifiable.
 *  • Enemies = a deliberately DIFFERENT, more menacing threat family
 *    (blood / infection / corruption — NO orange/amber/cyan/green) spread per
 *    archetype (value + temperature + saturation) so types read apart. The S80
 *    silhouettes already carry type-ID, so colour carries MOOD + threat tier.
 *  • Avatars factioned: VERA → the mainframe / player side; FENRIR → the enemy
 *    threat side.
 */
import { COLORS } from "./constants.ts";

// Re-export so a single import surfaces the whole governed palette.
export { COLORS };

// ═══════════════════ 1 · TOWER ELEMENTS (cool · varied · edel) ═══════════════
//
// Each element carries its chassis colours (used by range rings, projectiles,
// UI cards, glow) AND its emissive ramp (core → mid → rim, ART §6.1 / S79). The
// ramp `mid` IS the element identity hue (== designTokens.ELEMENT_COLORS).

export interface ElementPalette {
  /** Chassis body colour — range ring, projectile, UI card, node glow. */
  color: string;
  /** Lighter chassis accent — strokes, hot trails, charge cores. */
  colorLight: string;
  /** Emissive ramp core — near-white hot-spot, feeds the WebGL bloom. */
  core: string;
  /** Emissive ramp mid — the element IDENTITY hue (== ELEMENT_COLORS). */
  mid: string;
  /** Emissive ramp rim — dark grounded edge. */
  rim: string;
}

// `color` == `mid`: one identity hue per element (chassis = emissive identity).
// `core`/`rim` are deriveRamp(mid) — core = mid→white 60%, rim = mid→black 72%
// (the EmissiveMaterial transform; locked byte-for-byte by its test). To re-tune
// an element, change `mid` (+ pick a `colorLight`) and recompute core/rim.
//
// S139 / BL-132 / D-237 (the Glacier re-tone): each tower mid moves to its
// pastel-neon Glacier value; the silhouette (S80) + ramp formula (BL-42) are
// untouched, so the only thing that moves is the hue. `core`/`rim` recomputed
// from the new mid via deriveRamp; pre-computed values stored verbatim for
// the no-runtime-math contract.
export const TOWER_PALETTE: Record<string, ElementPalette> = {
  // water / ICE — pastel sky, the cool cluster's foundation (S139: was #00D4FF).
  water: {
    color: "#90C8F0",
    colorLight: "#BCDCF8",
    core: "#D3E9F9",
    mid: "#90C8F0",
    rim: "#283843",
  },
  // kinetic / Rail-Sentinel — pearl-pastel (the cool neutral; S139: was #AEBED2).
  kinetic: {
    color: "#D0D8E8",
    colorLight: "#E6EBF2",
    core: "#ECEFF6",
    mid: "#D0D8E8",
    rim: "#3A3C41",
  },
  // signal — pastel cool-violet broadcast (S139: was #7C5CD6).
  signal: {
    color: "#A898E8",
    colorLight: "#C4B8F0",
    core: "#DCD6F6",
    mid: "#A898E8",
    rim: "#2F2B41",
  },
  // fire / Malware — pastel cool rose, hot only in the white-ish core (S139: was #D2381B).
  fire: {
    color: "#E89A98",
    colorLight: "#F0BCBA",
    core: "#F6D7D6",
    mid: "#E89A98",
    rim: "#412B2B",
  },
  // lightning / EMP — pastel cool sand (S139: was #BF811D; cool sand replaces edel amber/brass).
  lightning: {
    color: "#E8D098",
    colorLight: "#F0E0BC",
    core: "#F6ECD6",
    mid: "#E8D098",
    rim: "#413A2B",
  },
  // virus / Contagion — pastel cool mint (S139: was #6FBF2E; cool mint replaces venom-green).
  virus: {
    color: "#98D8B0",
    colorLight: "#BCE6CC",
    core: "#D6EFDF",
    mid: "#98D8B0",
    rim: "#2B3C31",
  },
};

// ═══════════════ 2 · ENEMY THREAT FAMILY (blood · infection · corruption) ═════
//
// Deliberately DIFFERENT from the towers (no green/cyan/sand/mint). Spread
// per archetype for contrast; the S80 silhouette carries type-ID so the hue is
// free to carry mood + tier. Stealth = the BL-49 Prunkstück (kept as identity,
// re-toned to Glacier muted magenta).
//
// S139 / BL-132 / D-237 (the Glacier re-tone, D-224 INVARIANT preserved):
// the whole family stays distinctly **rose / magenta / cool-wine** — danger
// reads as danger — but each archetype's intensity is softened to the cool
// pastel direction. Boss/final_boss = the apex (hottest read). Spread per
// archetype kept: value (dark cool wine ↔ pale cool pink), temperature
// (cool rose ↔ cool plum) and saturation. Min ΔE within the family stays
// well above the BL-52 floor (>12) so types still read apart.
export const ENEMY_PALETTE: Record<string, string> = {
  scout: "#E090B0", // Data Packet — light, fast, low-threat cool pink-rose
  golem: "#5A2842", // Rootkit — heavy tank, deep cool wine (darkest)
  swarm: "#E8B8C8", // Nano-Swarm — many + weak, pale cool pink
  shifter: "#B870C8", // Polymorphic Worm — cool shifting magenta
  boss: "#D86880", // Core Breaker — cool crimson apex
  final_boss: "#D85878", // AXIOM-77 — the final apex (hottest cool rose)
  corrupted_node: "#C078A8", // ECHELON SIGNAL · muted cool dusty rose-magenta (the BL-128 hand-off)
  cipher_breaker: "#9078C0", // ECHELON DEEP · cool plum (veil outer · late-act veins)
  stealth_daemon: "#B070B0", // BL-49 Prunkstück cloak — cool muted magenta (identity preserved)
  firewall_breaker: "#B05068", // aggressive breaker — cool deep rose
  // BL-152 (S154) — the two new per-act threats, on the rose/magenta ECHELON
  // family (D-224 invariant). The Jammer = a vivid "jamming signal" magenta
  // (ECHELON turning your own defense against you); the Overseer = a deep,
  // commanding violet-plum (the Act III DOMINION aura-host). Both kept clearly
  // apart in hue/value from their neighbours (shifter/corrupted_node/cipher).
  jammer: "#D24FA0", // Act II — vivid cool magenta (the suppression signal)
  overseer: "#6E3C9E", // Act III — deep commanding violet-plum (DOMINION host)
  // BL-155 (S158) — Threat Pack 2, on the rose/magenta ECHELON family (D-224).
  // Each mechanic gets a distinct hue/value: the Leech = a hungry siphon
  // crimson-rose, the Regenerator = a self-knitting crystalline orchid, the
  // Splitter = a hot cloning pink, its Splinter sub = a pale child cousin.
  leech: "#D6486E", // Act II — hungry siphon crimson-rose (it bleeds your credits)
  regenerator: "#C56AD0", // Act III — crystalline orchid (the self-knitting wall)
  splitter: "#EE5C90", // Act II/III — hot cloning pink (it fractures into four)
  splitling: "#F4A6C4", // the Splinter sub — a pale child cousin of the Splitter
  // Task-009 (Spec B) — the three W20+ data archetypes, on the rose/magenta
  // ECHELON family (D-224 invariant). Hue slots chosen clear of their shape
  // twins so the reused silhouettes still read apart by colour: the
  // Hunter-Killer = a deep predatory raspberry-wine (vs shifter's cool
  // magenta), the Custodian = a pale glassy lavender-plum (vs cipher's plum /
  // regenerator's orchid), the Siphon = a deep vampiric wine-rose (vs leech's
  // brighter siphon crimson).
  hunter_killer: "#8E2456", // Act II — deep predatory raspberry (the shifting hunter)
  custodian: "#AC8CD8", // Act III — pale glassy lavender-plum (the healing vault)
  siphon: "#A22E4E", // Act III — deep vampiric wine-rose (it drains the wallet)
};

/** Polymorphic Worm shift tints, per mimicked element (Enemy.ts).
 *  Glacier (S139): slightly more saturated cousins of the new tower mids so
 *  the shift effect remains visible against the player tower it mimics. */
export const ENEMY_SHIFT_PALETTE: Record<string, string> = {
  fire: "#E8A088",
  water: "#78B8E8",
  lightning: "#E8C078",
};

// ═══════════════════ 3 · PLAYER DEFENSE (firewall / blocker) ══════════════════
// S139: colorLight re-toned to the Glacier mint-aqua light cousin.
export const DEFENSE = {
  firewall: { color: COLORS.SENTINEL_GREEN, colorLight: "#C4F0E8" },
} as const;

// ═══════════════════ 4 · SYNERGIES (link lines · center-node glow) ════════════
//
// BL-111 (S115-F40): synergies.json carried these 24 identity hex inline — the
// only data file that still did (I4 violation). Moved here verbatim
// (value-preserving, pixels identical); DataLoader.applyPalette() injects them
// keyed by synergy id, exactly like towers/enemies/firewall. Drawn by
// renderPipeline.renderSynergyLinks() (`syn.color` link strokes,
// `syn.colorLight` center-node glow).

export interface SynergyPalette {
  /** Link-line stroke colour — the synergy's identity hue. */
  color: string;
  /** Lighter accent — the pulsing center-node glow. */
  colorLight: string;
}

export const SYNERGY_PALETTE: Record<string, SynergyPalette> = {
  // RESONANCE is the one synergy NOT in synergies.json (built dynamically by
  // SynergyDetection for 3+ adjacent towers, which hardcoded these two hex
  // until BL-111). Its identity hue IS the ice-cyan anchor (S115-F40).
  // S139: colorLight re-toned to the Glacier ICE_CYAN light cousin.
  resonance: { color: COLORS.ICE_CYAN, colorLight: "#BCDCF8" },
  steam: { color: "#C8D4E4", colorLight: "#DEE6F0" },
  plasma: { color: "#E098C0", colorLight: "#EAB8D2" },
  electroshock: { color: "#90D8D8", colorLight: "#BCE8E4" },
  nexus: { color: "#FFFFFF", colorLight: "#F0F0FF" },
  tracking: { color: "#C8A8E8", colorLight: "#DCC4F0" },
  broadcast: { color: "#9888E0", colorLight: "#B0A4EA" },
  burning_mark: { color: "#E0AC8C", colorLight: "#EAC8AC" },
  designated: { color: "#B0D8EC", colorLight: "#D2E8F4" },
  railarc: { color: "#C0D898", colorLight: "#D8E4BC" },
  wildfire: { color: "#E09888", colorLight: "#EAB4A4" },
  waterborne: { color: "#88D8B8", colorLight: "#B4E6D0" },
  plague: { color: "#B088D8", colorLight: "#C8AAE4" },
};

// ═══════════════════ 5 · AVATARS (factioned · centrally governed) ═════════════
//
// VERA = the mainframe / player side; her HUD portrait FRAME tracks status
// (the frame IS the faction + status signal). FENRIR = the enemy threat side.
// `badge` tints the figure's backing emblem in the SVGs via `--avatar-badge`.
export const AVATAR_PALETTE = {
  vera: {
    idle: COLORS.SENTINEL_GREEN,
    cheer: COLORS.SENTINEL_GREEN,
    alert: COLORS.AMBER_WARN,
    alarm: COLORS.ORACLE_RED,
    down: COLORS.ORACLE_RED,
    wow: "#E8C098", // kernel_panic payload (deploy event) — Glacier legendary mid
    // S144 / BL-135 — frame status for the new VERA expressions (render-only).
    thinking: COLORS.ICE_CYAN, // analytic / reading the board — cool sky
    worried: COLORS.AMBER_WARN, // concern, the line is bending — amber
    proud: COLORS.SENTINEL_GREEN, // quiet approval — the player's green
    badge: "#406878", // backing diamond — her cool-teal tech side (S139 Glacier)
    badgeBack: "#1E3540", // backing triangle — deep cool teal (S139 Glacier)
  },
  fenrir: {
    frame: COLORS.ORACLE_RED,
    badge: COLORS.ORACLE_RED, // backing diamond — the enemy threat side
  },
} as const;

// ═══════════════════ 6 · SKILL RARITY (card material tiers, BL-54) ════════════
//
// THE single source for skill-card rarity — colour + material escalation. It was
// hardcoded TWICE before (a `RARITY_COLORS` copy in both `DraftUI.ts` and
// `SkillOverview.ts`), a second colour authority (the I4 "one colour source"
// drift), AND two of the four hues WERE on-screen gameplay identity (common ==
// SENTINEL_GREEN, the player; rare == ICE_CYAN, the water tower) so rarity read
// as faction/element, not tier.
//
// Owner-signed Option A (docs/skill_rarity_swatch.html, 2026-06-05): keep the
// player-readable convention (steel → azure → violet → gold) but move it OFF the
// identity collisions, and let the TIER read through a real engine-material
// escalation — `drawLitPanel` edge `intensity` + the shared `UIBloom` weight +
// a legendary scanline the WebGL bloom flares on reveal. Legendary = the apex.
//
// `mid` is the identity hue; `core`/`rim` are `deriveRamp(mid)` at the draw site
// (the same EmissiveMaterial transform the arena uses, so a palette nudge here
// re-harmonises the whole tier automatically). Reusable for BL-55/BL-56 chips.

export type RarityName = "common" | "rare" | "epic" | "legendary";
export type RarityTreatment = "none" | "pulse" | "apex";

export interface RarityMaterial {
  /** Identity mid hue — feeds `deriveRamp(mid)` → {core,mid,rim} at the draw site. */
  mid: string;
  /** Lit-edge intensity passed to `drawLitPanel` (the §3 escalation). */
  intensity: number;
  /** Shared-`UIBloom` weight 0..1 — 0 = no bloom seed (common stays flat-lit). */
  bloomWeight: number;
  /** Per-tier extra: `pulse` = epic settle-pulse, `apex` = legendary scanline+flare. */
  treatment: RarityTreatment;
}

/** The four rarity material tiers (Option A mids + the §3 escalation).
 *  S139 / BL-132: mids re-toned to Glacier (cool grey · cool azure · cool
 *  violet · cool amber). The intensity / bloomWeight / treatment escalation
 *  is untouched — it's a material parameter, not a colour. */
export const RARITY: Record<RarityName, RarityMaterial> = {
  common: { mid: "#98A0B0", intensity: 0.7, bloomWeight: 0, treatment: "none" },
  rare: {
    mid: "#80A8E0",
    intensity: 1.0,
    bloomWeight: 0.45,
    treatment: "none",
  },
  epic: {
    mid: "#A088D8",
    intensity: 1.35,
    bloomWeight: 0.7,
    treatment: "pulse",
  },
  legendary: {
    mid: "#E8C098",
    intensity: 1.8,
    bloomWeight: 1.0,
    treatment: "apex",
  },
} as const;

/** Resolve a rarity string (possibly unknown) to its material tier — common is
 *  the safe fallback. The ONE place a card's rarity becomes colour + material. */
export function rarityMaterial(rarity: string): RarityMaterial {
  return RARITY[rarity as RarityName] ?? RARITY.common;
}

/** Per-rarity HUD chip / slot accent — the brighter readout hues used by the
 *  skill-deck + payload-rack chips (and the payload aim reticle). Distinct from
 *  `RARITY.mid` (the lit card material). ONE source — previously copied into
 *  SkillDeckBar + PayloadRackBar.
 *  S139: common/rare track the new SENTINEL/ICE identities; epic mirrors the
 *  new rarity epic mid; legendary tracks the new AMBER family. */
export const RARITY_SLOT_COLORS: Record<string, string> = {
  common: "#88E0D0",
  rare: "#90C8F0",
  epic: "#A088D8",
  legendary: "#E8D098",
};
